/**
 * Lead Detail Page
 * Dedicated page for viewing individual lead details with Sales Assist
 * Route: /leads/:lead_id (via query param ?page=lead-detail&lead=<id>)
 */
import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { ArrowLeft, Phone, Mail, Car, Calendar, MapPin, ExternalLink, FlaskConical, RotateCw } from "lucide-react";
import { SalesAssistPanel } from "../components/dashboard/SalesAssistPanel";
import InfoTooltip from "../components/InfoTooltip";

/**
 * Format vehicle identifier for display
 * Converts internal prefixes to user-friendly labels
 */
// iter88 — Issue 12 helper: returns true when a string looks like a
// trim level (short, alpha-only, no digits) rather than a stock #.
// Stock numbers in the wild almost always contain digits and are
// usually ≥4 chars. Trim codes are things like EX, LX, SR5, Lariat,
// SE, Touring, etc. The embed mis-captures these into both vin and
// stock_number fields, so we apply this filter at both render sites.
const looksLikeTrim = (s) =>
  !!s && /^[A-Za-z][A-Za-z\s-]{0,8}$/.test(s) && !/\d/.test(s);

const formatVehicleId = (rawId) => {
  if (!rawId) return null;
  
  // Remove internal prefixes and format for display
  if (rawId.startsWith('VEHICLE_ID:')) {
    const id = rawId.replace('VEHICLE_ID:', '');
    return { type: 'Stock #', value: id, isVin: false };
  }
  if (rawId.startsWith('ID:')) {
    const id = rawId.replace('ID:', '');
    return { type: 'ID', value: id, isVin: false };
  }
  if (rawId.startsWith('PATH:')) {
    const id = rawId.replace('PATH:', '');
    return { type: 'Ref', value: id, isVin: false };
  }
  if (rawId.startsWith('UNKNOWN:')) {
    return null; // Don't display unknown identifiers
  }
  
  // Check if it's a valid 17-char VIN
  const isValidVin = /^[A-HJ-NPR-Z0-9]{17}$/i.test(rawId);
  if (isValidVin) {
    return { type: 'VIN', value: rawId, isVin: true };
  }
  
  // iter88 — Issue 12: short alpha-only strings (e.g. "EX", "LX", "SR5",
  // "Lariat") are trim level codes the embed mistakenly captured into
  // the VIN field. Never label these as "Stock #". Real stock numbers
  // are typically ≥4 chars and almost always contain digits.
  if (looksLikeTrim(rawId)) {
    return null; // Trim is rendered above by lead.vehicle_trim; don't double-show it as Stock #.
  }
  
  // Fallback: display as-is if it looks like a stock number
  return { type: 'Stock #', value: rawId, isVin: false };
};

const LeadDetailPage = ({ leadId, onNavigateBack }) => {
  const { apiFetch, token } = useAuth();
  const toast = useToast();
  const [lead, setLead] = useState(null);
  const [salesAssistData, setSalesAssistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingTestFlag, setSavingTestFlag] = useState(false);

  // iter94 — Issue 14: Mark/unmark lead as Test/Demo
  const toggleTestFlag = async () => {
    if (!lead || savingTestFlag) return;
    const next = !lead.is_test;
    setSavingTestFlag(true);
    try {
      await apiFetch(`/unlock/leads/${lead.id}/test-flag`, {
        method: "PATCH",
        data: { is_test: next },
      });
      setLead({ ...lead, is_test: next });
      toast.success(next ? "Marked as Test/Demo" : "Unmarked as Test/Demo");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update test flag");
    } finally {
      setSavingTestFlag(false);
    }
  };

  useEffect(() => {
    const fetchLead = async () => {
      if (!leadId) return;
      
      try {
        setLoading(true);
        // Try to fetch from unlocked leads first, then regular leads
        let leadData = null;
        
        try {
          const unlockedRes = await apiFetch("/unlock/leads", { method: "GET" });
          if (unlockedRes.data?.leads) {
            leadData = unlockedRes.data.leads.find(l => l.id === leadId);
          }
        } catch (e) {
          console.log("Unlocked leads not available, trying regular leads");
        }
        
        if (!leadData) {
          const leadsRes = await apiFetch("/leads", { method: "GET" });
          if (leadsRes.data) {
            leadData = leadsRes.data.find(l => l.id === leadId);
          }
        }
        
        if (leadData) {
          setLead(leadData);
          setError(null);
          
          // Also fetch Sales Assist data for vehicle context enrichment
          try {
            const salesAssistRes = await apiFetch(`/dashboard/leads/${leadId}/sales-assist`, { method: "GET" });
            if (salesAssistRes.data) {
              setSalesAssistData(salesAssistRes.data);
              
              // Enrich lead with primary vehicle data if missing
              if (!leadData.vehicle_year && !leadData.vehicle_make && salesAssistRes.data.primary_vehicle) {
                setLead(prev => ({
                  ...prev,
                  vehicle_year: salesAssistRes.data.primary_vehicle.year,
                  vehicle_make: salesAssistRes.data.primary_vehicle.make,
                  vehicle_model: salesAssistRes.data.primary_vehicle.model,
                  vehicle_trim: salesAssistRes.data.primary_vehicle.trim,
                  vehicle_price: salesAssistRes.data.primary_vehicle.price,
                }));
              }
            }
          } catch (e) {
            console.log("Sales assist data not available:", e);
          }
        } else {
          setError("Lead not found");
        }
      } catch (e) {
        console.error("Error fetching lead:", e);
        setError("Failed to load lead details");
      } finally {
        setLoading(false);
      }
    };
    
    fetchLead();
  }, [leadId, apiFetch]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="page" data-testid="lead-detail-page">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page" data-testid="lead-detail-page">
        <button 
          onClick={onNavigateBack}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          data-testid="back-to-leads-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Leads
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page" data-testid="lead-detail-page">
      {/* Header */}
      <div className="mb-6">
        <button 
          onClick={onNavigateBack}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          data-testid="back-to-leads-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Leads
        </button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900 m-0">
            {lead?.first_name || lead?.name || "Lead"} {lead?.last_name || ""}
          </h1>
          {/* iter94 — Issue 14: Mark as Test/Demo */}
          {lead?.is_test && (
            <span
              data-testid="lead-detail-test-badge"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide text-amber-800 bg-amber-100 border border-amber-300"
            >
              <FlaskConical className="w-3 h-3" /> Test / Demo
            </span>
          )}
          {/* iter96 — Issue 15: multiple price attempts indicator */}
          {(lead?.price_attempt_count || 0) > 1 && (
            <span
              data-testid="lead-detail-multi-attempt-badge"
              title={`This shopper has inquired about pricing ${lead.price_attempt_count} times on the same or different vehicles. We don't dedupe — each entry is a real interest signal.`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide text-purple-800 bg-purple-50 border border-purple-200"
            >
              <RotateCw className="w-3 h-3" /> {lead.price_attempt_count}× price attempts
            </span>
          )}
          <button
            onClick={toggleTestFlag}
            disabled={savingTestFlag}
            data-testid="lead-detail-mark-test-btn"
            className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-semibold border transition-colors disabled:opacity-60 ${
              lead?.is_test
                ? "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                : "border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100"
            }`}
          >
            <FlaskConical className="w-3.5 h-3.5" />
            {savingTestFlag
              ? "Saving…"
              : lead?.is_test ? "Unmark as Test" : "Mark as Test"}
          </button>
        </div>
        {/* iter88 — Issue 21: the raw UUID lead ID is no longer a
            primary heading-level element. It moved down to a
            collapsed "Technical Details" disclosure at the bottom of
            the page so dealers see customer info first. */}
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Lead Details */}
        <div className="lg:col-span-1 space-y-6">
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div className="space-y-3">
              {(lead?.phone) && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">{lead.phone}</span>
                  {lead?.verified_mobile && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                      Verified
                    </span>
                  )}
                </div>
              )}
              {(lead?.email) && (
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">{lead.email}</span>
                </div>
              )}
              {(lead?.visitor_zip) && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">ZIP: {lead.visitor_zip}</span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700">Created: {formatDate(lead?.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Vehicle Interest - YMM and price primary, VIN secondary */}
          {(lead?.vin || lead?.vehicle_make) && (
            <div className="bg-white rounded-lg shadow p-6" data-testid="vehicle-interest-section">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Car className="w-5 h-5 text-gray-500" />
                Vehicle Interest
              </h3>
              <div className="space-y-3">
                {/* Year/Make/Model - PRIMARY (P1-A requirement) */}
                {(lead?.vehicle_year || lead?.vehicle_make || lead?.vehicle_model) ? (
                  <div data-testid="vehicle-ymm">
                    <p className="text-lg font-semibold text-gray-900">
                      {[lead?.vehicle_year, lead?.vehicle_make, lead?.vehicle_model]
                        .filter(Boolean)
                        .join(" ")}
                    </p>
                    {/* iter88 — Issue 12: prefer vehicle_trim; fall back to
                        stock_number if it looks like a trim (e.g. "EX"
                        for a 2022 Honda Accord) so we don't double-
                        mislabel it as "Stock #" further down. */}
                    {(() => {
                      const trimDisplay = lead?.vehicle_trim
                        || (looksLikeTrim(lead?.stock_number) ? lead.stock_number : null);
                      if (!trimDisplay) return null;
                      return (
                        <p className="text-sm text-gray-600">
                          <span className="text-gray-500">Trim:</span>{" "}
                          <span className="font-medium text-gray-800">{trimDisplay}</span>
                        </p>
                      );
                    })()}
                  </div>
                ) : (
                  /* Graceful fallback when YMM unavailable */
                  lead?.vin && (
                    <div data-testid="vehicle-vin-only">
                      <p className="text-gray-500 text-sm">Vehicle details loading...</p>
                    </div>
                  )
                )}
                {/* Price - PROMINENT */}
                {lead?.vehicle_price && (
                  <div className="bg-green-50 rounded-lg p-3 inline-block" data-testid="vehicle-price">
                    <p className="text-xl font-bold text-green-700">
                      ${lead.vehicle_price.toLocaleString()}
                    </p>
                  </div>
                )}
                {/* Stock Number — iter88 Issue 12: hide when it looks
                    like a trim level (already promoted above). */}
                {lead?.stock_number && !looksLikeTrim(lead.stock_number) && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500">Stock #:</span>
                    <span className="font-medium text-gray-900">{lead.stock_number}</span>
                  </div>
                )}
                {/* Vehicle Identifier - VIN displayed below YMM */}
                {lead?.vin && (
                  (() => {
                    const formatted = formatVehicleId(lead.vin);
                    if (!formatted) return null;
                    return (
                      <div className="pt-2 border-t border-gray-100" data-testid="vehicle-vin">
                        <p className="text-xs text-gray-500 mb-1">{formatted.type}</p>
                        <code className={`text-xs px-2 py-1 rounded ${formatted.isVin ? 'bg-gray-100 text-gray-600' : 'bg-blue-50 text-blue-700'}`}>
                          {formatted.value}
                        </code>
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          )}

          {/* Intent & Status */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Lead Status</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Type</span>
                <span className={`px-2 py-1 rounded text-sm font-medium ${
                  lead?.lead_type === "unlock" 
                    ? "bg-amber-100 text-amber-800" 
                    : "bg-gray-100 text-gray-700"
                }`}>
                  {lead?.lead_type === "unlock" ? "Unlocked" : "Standard"}
                </span>
              </div>
              {lead?.intent_score_at_submit !== null && lead?.intent_score_at_submit !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 inline-flex items-center">
                    Intent Score
                    {/* iter98 — Tooltips: explain Intent Score scale */}
                    <InfoTooltip testId="tooltip-intent-score" maxWidth={300}>
                      Intent Score reflects how engaged this shopper is, based on pages visited, time on site, vehicles viewed, and return visits. Scale: 1 (low) to 10 (high). Scores of 7+ indicate high purchase intent.
                    </InfoTooltip>
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    lead.intent_score_at_submit >= 70 
                      ? "bg-green-100 text-green-700" 
                      : lead.intent_score_at_submit >= 40 
                        ? "bg-yellow-100 text-yellow-700" 
                        : "bg-gray-100 text-gray-600"
                  }`}>
                    {lead.intent_score_at_submit}
                  </span>
                </div>
              )}
              {lead?.unlocked_at && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Unlocked</span>
                  <span className="text-gray-700">{formatDate(lead.unlocked_at)}</span>
                </div>
              )}
              {lead?.utm_source && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Source</span>
                  <span className="text-gray-700">{lead.utm_source}</span>
                </div>
              )}
              {lead?.utm_campaign && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Campaign</span>
                  <span className="text-gray-700">{lead.utm_campaign}</span>
                </div>
              )}
              {/* P2: Attribution Capture - gclid/fbclid */}
              {lead?.gclid && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Google Click ID</span>
                  <span className="text-xs text-gray-500 font-mono truncate max-w-[150px]" title={lead.gclid}>
                    {lead.gclid.substring(0, 20)}...
                  </span>
                </div>
              )}
              {lead?.fbclid && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Facebook Click ID</span>
                  <span className="text-xs text-gray-500 font-mono truncate max-w-[150px]" title={lead.fbclid}>
                    {lead.fbclid.substring(0, 20)}...
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* P2: Full Attribution Details (shown if any attribution exists) */}
          {(lead?.gclid || lead?.fbclid || lead?.referrer || lead?.landing_page) && (
            <div className="bg-white rounded-lg shadow p-6 mt-4" data-testid="attribution-section">
              <h3 className="font-semibold text-gray-900 mb-4">Attribution</h3>
              <div className="space-y-2 text-sm">
                {lead?.gclid && (
                  <div>
                    <span className="text-gray-500">GCLID: </span>
                    <span className="font-mono text-xs text-gray-700 break-all">{lead.gclid}</span>
                  </div>
                )}
                {lead?.fbclid && (
                  <div>
                    <span className="text-gray-500">FBCLID: </span>
                    <span className="font-mono text-xs text-gray-700 break-all">{lead.fbclid}</span>
                  </div>
                )}
                {lead?.referrer && (
                  <div>
                    <span className="text-gray-500">Referrer: </span>
                    <span className="text-gray-700 break-all">{lead.referrer}</span>
                  </div>
                )}
                {lead?.landing_page && (
                  <div>
                    <span className="text-gray-500">Landing Page: </span>
                    <a 
                      href={lead.landing_page} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline break-all"
                    >
                      {lead.landing_page}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Sales Assist Panel */}
        <div className="lg:col-span-2">
          <SalesAssistPanel 
            token={token} 
            leadId={leadId}
          />
        </div>
      </div>

      {/* iter88 — Issue 21: Technical Details disclosure. Hides the raw
          UUID and any other internal identifiers from the primary
          customer view but keeps them one click away for support. */}
      <details className="mt-6 bg-gray-50 rounded-lg border border-gray-200" data-testid="lead-technical-details">
        <summary className="cursor-pointer px-4 py-3 text-sm text-gray-600 hover:text-gray-900 select-none">
          Technical Details
        </summary>
        <div className="px-4 pb-4 pt-2 text-xs text-gray-500 space-y-1.5 font-mono">
          <div>
            <span className="text-gray-400 not-italic font-sans">Lead ID:</span>{" "}
            <code className="text-gray-700">{leadId}</code>
          </div>
          {lead?.business_id && (
            <div>
              <span className="text-gray-400 not-italic font-sans">Business ID:</span>{" "}
              <code className="text-gray-700">{lead.business_id}</code>
            </div>
          )}
          {lead?.visitor_id && (
            <div>
              <span className="text-gray-400 not-italic font-sans">Visitor ID:</span>{" "}
              <code className="text-gray-700">{lead.visitor_id}</code>
            </div>
          )}
          {lead?.session_id && (
            <div>
              <span className="text-gray-400 not-italic font-sans">Session ID:</span>{" "}
              <code className="text-gray-700">{lead.session_id}</code>
            </div>
          )}
        </div>
      </details>
    </div>
  );
};

export default LeadDetailPage;
